import { postForm, request } from './client';

/**
 * upload service - wraps upload endpoints using centralized client
 * Migrated from src/utils/upload.ts
 *
 * Notes:
 * - The endpoint '/api/upload' is a placeholder. Replace if your backend uses a different route.
 * - Expectation: the upload endpoint returns JSON like { url: string } for the uploaded file.
 */

/**
 * Upload a single File and return the remote URL.
 */
export async function uploadImageFile(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  // 使用项目提供的自建图床（文档：https://imageproxy.zhongzhuan.chat/api/upload）
  // 保持与图床 API 返回格式一致：{ url: string, created?: number }
  const j = await postForm<{ url?: string }>('https://imageproxy.zhongzhuan.chat/api/upload', fd, {
    includeAuth: false,
    resolveEndpoint: false,
  });
  if (!j?.url) throw new Error('上传接口未返回 url 字段');
  return j.url as string;
}

/**
 * Ensure the provided source is a URL:
 * - If input is a dataURL or File, upload it and return the remote URL.
 * - If input is already an http(s) URL, return it directly.
 */
export async function ensureImageUrl(srcOrFile: File | string): Promise<string> {
  if (!srcOrFile) throw new Error('invalid image source');
  if (typeof srcOrFile === 'string') {
    // data URL
    if (srcOrFile.startsWith('data:')) {
      // convert to blob then File
      const blob = await request<Blob>({
        method: 'GET',
        endpoint: srcOrFile,
        responseType: 'blob',
        includeAuth: false,
        resolveEndpoint: false,
      });
      const fileType = blob.type || 'image/png';
      const f = new File([blob], 'image.png', { type: fileType });
      return await uploadImageFile(f);
    }
    // assume remote URL already
    return srcOrFile;
  } else {
    // File
    return await uploadImageFile(srcOrFile);
  }
}

/**
 * Upload all slots (each slot may have .file or .src). Returns array of URLs in same order.
 */
export async function uploadAllSlots(slots: { id: string; src: string; file?: File }[]): Promise<string[]> {
  const urls: string[] = [];
  for (const s of slots) {
    if (s.file) {
      const url = await uploadImageFile(s.file);
      urls.push(url);
    } else if (s.src) {
      const u = await ensureImageUrl(s.src);
      urls.push(u);
    } else {
      urls.push('');
    }
  }
  return urls;
}
